/* eslint-disable camelcase */
/* eslint-disable  @typescript-eslint/no-non-null-asserted-optional-chain */
import { useMutation } from '@tanstack/react-query'
import { aquaApi } from '../../../../api/aquaApi'
import { paths } from '../../../../api/pathsApi'
import { handleAxiosCodeError, handleAxiosError } from '../../../../helpers/handleAxiosError'
import { AxiosError } from 'axios'
import { useContext, useRef } from 'react'
import { NecropsyContext } from '../../../../context/Necropsy/Provider'
import { formatDateForSend } from '../../helpers'
import { CaseRespose, RecordsNumber } from '../types'
import { useBlobAzure } from '../../../../hooks/useAzureSaveImage'

interface Args {
  onSuccess: (value: RecordsNumber[]) => void
  onError: (error: AxiosError, body: string[]) => void
}

interface Body {
  case_id: number
  record_date: string
  image_type: string
  image_url: string
}

const saveData = async (body: Body) => {
  try {
    const { data } = await aquaApi.post<CaseRespose>(`${paths().HEALT.CREATE_RECORD}`, body)
    return data
  } catch (error) {
    console.error('El error es ', (error as AxiosError).response?.request)
    throw error
  }
}
export const useSendData = ({ onError, onSuccess }: Args) => {
  const { getRecordById } = useContext(NecropsyContext)
  const recordsNumber = useRef<RecordsNumber[]>([])
  const { isUploading, uploadFiles } = useBlobAzure({
    containerName: 'necropsy',
  })
  const { data, error, isPending, mutate, isSuccess } = useMutation({
    mutationFn: async (requests: Body[]) => {
      const results = await Promise.all(requests.map(saveData))
      return results
    },
    onSuccess() {
      onSuccess(recordsNumber.current)
    },
    onError: (error: AxiosError) => {
      console.log(error)
      onError(
        error,
        recordsNumber.current.map((val) => val.caseNumber),
      )
    },
  })

  const handleSendData = (values: RecordsNumber[]) => {
    recordsNumber.current = values
    values.forEach(async ({ caseId, caseNumber }) => {
      const record = getRecordById(caseNumber)

      const { internalPhoto, externalPhoto, gillPhoto, additionalPhotos } = record
      const fixedPhotoCount = 3
      const requests = [
        {
          uri: externalPhoto.uri,
          type: 'externalPhoto',
          time: formatDateForSend(
            `${externalPhoto.dateTime?.date!} ${externalPhoto.dateTime?.time}`,
          ),
          name: `${caseNumber}-1.jpg`,
        },
        {
          uri: gillPhoto.uri,
          type: 'gillPhoto',
          time: formatDateForSend(`${gillPhoto.dateTime?.date!} ${gillPhoto.dateTime?.time}`),
          name: `${caseNumber}-2.jpg`,
        },
        {
          uri: internalPhoto.uri,
          type: 'internalPhoto',
          time: formatDateForSend(
            `${internalPhoto.dateTime?.date!} ${internalPhoto.dateTime?.time}`,
          ),
          name: `${caseNumber}-3.jpg`,
        },

        ...Object.values(additionalPhotos).map((photo, index) => ({
          uri: photo.uri,
          type: 'additionalPhoto',
          time: formatDateForSend(`${photo.dateTime?.date!} ${photo.dateTime?.time}`),
          name: `${caseNumber}-${fixedPhotoCount + index + 1}.jpg`,
        })),
      ]

      const azureResponse = await uploadFiles(
        requests.map((image) => ({ imageUri: image.uri!, imageName: image.name! })),
      )

      if (azureResponse.length === 0) {
        onError(
          {
            message: 'Error enviando las imagenes a Azure',
            isAxiosError: true,
            toJSON: () => ({}),
            name: 'Error',
          },
          values.map((val) => val.caseNumber),
        )
        return
      }

      mutate(
        azureResponse.map((imageUrl, index) => ({
          case_id: caseId,
          image_type: requests[index]?.type ?? '',
          image_url: imageUrl,
          record_date: requests[index]?.time ?? '',
        })),
      )
    })
  }

  return {
    handleSendData,
    data,
    isLoading: isPending || isUploading,
    isSuccess,
    msgError: handleAxiosError(error as AxiosError),
    codeError: handleAxiosCodeError(error as AxiosError),
  }
}
